import Notifications from 'react-notification-system-redux';
import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';

import { closeModal } from '../../containers/Full/actions';
import { getRequestErrorMessage } from '../../utilities/SagasHelper';
import * as api from '../../utilities/ServiceManager';
import {
  setUpdateChapterAudioSummary,
  setUpdateChapterPlaylist,
  updateReference,
} from '../Bibliographies/actions';
import {
  GET_UPDATE_CHAPTER_AUDIO_SUMMARY,
  GET_UPDATE_CHAPTER_PLAYLIST,
} from '../Bibliographies/actionTypesSagas';
import { loadQuestionsOfChapter } from '../Questions/actionsSagas';
import {
  addFilter,
  editFilter as updateFilterAction,
  removeFilter,
  setAudioPlaylist,
  setAudioPlaylistState,
  setBooksModalOpeningPending,
  setFilters,
  setFinishProcessing,
  setLogs,
  setNamesToBuildSecondBar,
  setQuestionCsvByDetail,
  setQuestionsCSV,
  updateWaiting,
} from './actions';
import {
  CREATE_FILTER,
  DELETE_FILTER,
  EDIT_FILTER,
  EXPORT_CHAPTER_TO_CSV_REQUEST,
  EXPORT_QUESTIONS_TO_CSV_REQUEST,
  EXPORT_REFERENCE_TO_CSV_REQUEST,
  GET_CHAPTER_AUDIO_PLAYLIST,
  GET_LOGS,
  GET_NAMES_TO_SECOND_BAR,
  IMPORT_CHAPTER_FROM_CSV_REQUEST,
  IMPORT_QUESTIONS_FROM_CSV_REQUEST,
  IMPORT_REFERENCE_FROM_CSV_REQUEST,
  LOAD_CHAPTER_QUESTIONS_CSV,
  LOAD_FILTERS,
  SET_ACTIVE_FILTER,
  UPDATE_SP_REFERENCE,
} from './actionTypesSagas';
import {
  CHAPTER_AUDIO_PLAYLIST_ERROR,
  CHAPTER_AUDIO_PLAYLIST_GENERATION_PENDING,
  CHAPTER_AUDIO_PLAYLIST_LOADING,
  CHAPTER_AUDIO_PLAYLIST_SUCCESS,
} from './constants';

function* getLogsSagas() {
  yield takeEvery(GET_LOGS, getLogs);
}

function* getLogs(action) {
  let logs;
  yield put(updateWaiting(true));
  try {
    if (action.isChapter) {
      logs = yield call(api.getChapterLogs, action.id);
    } else {
      logs = yield call(api.getReferenceLogs, action.id);
    }

    if (logs) {
      yield put(setLogs(logs));
    } else {
      yield put(setLogs([]));
    }

    yield put(updateWaiting(false));
  } catch (exception) {
    yield put(
      showNotificationErrorFromException(
        exception,
        'Something happend trying to get activity. Please try again later.',
      ),
    );
    yield put(updateWaiting(false));
  }
}

/**
 * Update study programs of reference
 */
function* updateStudyProgramsSagas() {
  yield takeLatest(UPDATE_SP_REFERENCE, callUpdateStudyPrograms);
}

function* callUpdateStudyPrograms(action) {
  try {
    yield put(updateWaiting(true));
    const reference = yield call(
      api.updateStudyProgramsReference,
      action.studyPrograms,
      action.reference,
    );
    yield put(updateReference(reference));
    yield put(
      Notifications.show(
        {
          title: 'Done!',
          message: 'Study programs updated successfully!',
          autoDismiss: 1,
        },
        'success',
      ),
    );
  } catch (exception) {
    yield put(
      showNotificationErrorFromException(
        exception,
        'Could not load update study programs. Please try again later.',
      ),
    );
  } finally {
    yield put(updateWaiting(false));
    yield put(closeModal(true));
  }
}

/**
 * Load adminuser filters
 */
function* loadFiltersSagas() {
  yield takeLatest(LOAD_FILTERS, callLoadFilters);
}

function* callLoadFilters(action) {
  try {
    const user = yield call(api.getUserFilters, action.user);
    yield put(setFilters(user.filters));
  } catch (exception) {
    yield put(
      showNotificationErrorFromException(
        exception,
        'Could not load filters. Please try again later.',
      ),
    );
  } finally {
  }
}

/**
 * Create filter
 */
function* createFilterSagas() {
  yield takeLatest(CREATE_FILTER, callCreateFilter);
}

function* callCreateFilter(action) {
  try {
    const filter = yield call(api.createFilter, action.filter);
    yield put(addFilter(filter));
    yield put(
      Notifications.show(
        {
          title: 'Done!',
          message: 'Filter created successfully!',
          autoDismiss: 1,
        },
        'success',
      ),
    );
  } catch (exception) {
    yield put(
      showNotificationErrorFromException(
        exception,
        'Could not create filter. Please try again later.',
      ),
    );
  } finally {
  }
}

/**
 * Edit filter
 */
function* editFilterSagas() {
  yield takeLatest(EDIT_FILTER, callEditFilter);
}

function* callEditFilter(action) {
  try {
    const updatedFilter = action.filter;
    delete updatedFilter.adminUsers;
    const filter = yield call(api.updateFilter, updatedFilter);
    yield put(updateFilterAction(filter));
  } catch (exception) {
    yield put(
      showNotificationErrorFromException(
        exception,
        'Could not edit filter. Please try again later.',
      ),
    );
  } finally {
  }
}

/**
 * Set active filter
 */
function* setActiveFilterSagas() {
  yield takeLatest(SET_ACTIVE_FILTER, callSetActiveFilter);
}

function* callSetActiveFilter(action) {
  try {
    const activeFilter = action.filter;
    const filter = yield call(api.setActiveFilter, activeFilter);
    yield put(updateFilterAction(filter));
  } catch (exception) {
    yield put(
      showNotificationErrorFromException(
        exception,
        'Could not edit filter. Please try again later.',
      ),
    );
  } finally {
  }
}

/**
 * Delete filter
 */
function* deleteFilterSagas() {
  yield takeLatest(DELETE_FILTER, callDeleteFilter);
}

function* callDeleteFilter(action) {
  try {
    yield call(api.deleteFilter, action.filterId);
    yield put(removeFilter(action.filterId));
    yield put(
      Notifications.show(
        {
          title: 'Done!',
          message: 'Filter deleted successfully!',
          autoDismiss: 1,
        },
        'success',
      ),
    );
  } catch (exception) {
    yield put(
      showNotificationErrorFromException(
        exception,
        'Could not delete filter. Please try again later.',
      ),
    );
  } finally {
  }
}

/**
 * Load chapter questions csv
 */
function* loadChapterQuestionsCSVSagas() {
  yield takeLatest(LOAD_CHAPTER_QUESTIONS_CSV, loadChapterQuestionsCSV);
}

function* loadChapterQuestionsCSV(action) {
  try {
    const questionsArray = [];

    const csv = yield call(
      api.getChapterQuestionsCSV,
      action.chapter,
      action.isAssignment,
    );

    const { report, totalQuestions } = csv;

    questionsArray.push(...report);

    if (totalQuestions > questionsArray.length) {
      yield put(
        Notifications.show(
          {
            title: 'Loading',
            message: 'Loading questions CSV...',
            autoDismiss: 2,
          },
          'info',
        ),
      );

      for (let i = 10; i < totalQuestions; i += 10) {
        const response = yield call(
          api.getChapterQuestionsCSV,
          action.chapter,
          action.isAssignment,
          i,
        );

        questionsArray.push(...response.report);
      }
    }

    const sortedQuestionsByID = questionsArray.sort(
      (a, b) => a.questionID - b.questionID,
    );

    if (action.byDetail) {
      yield put(
        setQuestionCsvByDetail(addLiteralToDocumentPage(sortedQuestionsByID)),
      );
    } else {
      yield put(setQuestionsCSV(addLiteralToDocumentPage(sortedQuestionsByID)));
    }

    yield put(
      Notifications.show(
        {
          title: 'Done!',
          message: 'File generated successfully!',
          autoDismiss: 3,
        },
        'success',
      ),
    );
  } catch (exception) {
    put(
      showNotificationErrorFromException(
        exception,
        'Could not load the question CSV. Please try again later.',
      ),
    );
  } finally {
    yield put(setFinishProcessing(true));
  }
}

function* getNamesToSecondBarSagas() {
  yield takeLatest(GET_NAMES_TO_SECOND_BAR, getNamesToSecondBar);
}

function* getNamesToSecondBar(action) {
  try {
    const response = yield call(api.getNamesToBuildSecondBar, action.letter);
    yield put(setNamesToBuildSecondBar(response));
  } catch (err) {
  } finally {
  }
}

function addLiteralToDocumentPage(allQuestion) {
  // we add a ' at the beginning of the document page to avoid excel to use this column as date
  if (allQuestion.length) {
    return allQuestion.map((question) => {
      if (question.documentpage) {
        question.documentpage = `'${question.documentpage}`;
      }
      return question;
    });
  }
  return allQuestion;
}

function* getChapterAudioPlaylistWorkerSagas(action) {
  try {
    yield put(setAudioPlaylistState(CHAPTER_AUDIO_PLAYLIST_LOADING));
    const playlist = yield call(api.getAudioPlaylist, action.payload.chapterId);
    yield put(setAudioPlaylist(playlist));
    yield put(setAudioPlaylistState(CHAPTER_AUDIO_PLAYLIST_SUCCESS));
  } catch {
    yield put(setAudioPlaylistState(CHAPTER_AUDIO_PLAYLIST_ERROR));
  }
}

function* getChapterAudioPlaylistWatcherSagas() {
  yield takeLatest(
    GET_CHAPTER_AUDIO_PLAYLIST,
    getChapterAudioPlaylistWorkerSagas,
  );
}

function* updateChapterPlaylistWorkerSaga(action) {
  try {
    if (Array.isArray(action.chapterId)) {
      for (const chapterId of action.chapterId) {
        const playlist = yield call(api.getAudioPlaylist, chapterId);
        yield put(
          setUpdateChapterPlaylist(playlist, chapterId, action.referenceId),
        );
      }
      yield put(setBooksModalOpeningPending(true));
      return;
    }

    const playlist = yield call(api.getAudioPlaylist, action.chapterId);
    yield put(
      setUpdateChapterPlaylist(playlist, action.chapterId, action.referenceId),
    );
    yield put(setBooksModalOpeningPending(true));
  } catch (err) {
    yield put(
      showNotificationErrorFromException(
        err,
        'Something happend trying to get updated playlist.',
      ),
    );
  }
}

function* updateChapterPlaylistWatcherSaga() {
  yield takeLatest(
    GET_UPDATE_CHAPTER_PLAYLIST,
    updateChapterPlaylistWorkerSaga,
  );
}

function* updateChapterAudioSummaryWorkerSaga(action) {
  try {
    const audioSummary = yield call(api.getAudioSummary, action.chapterId);
    yield put(
      setUpdateChapterAudioSummary(
        audioSummary,
        action.chapterId,
        action.referenceId,
      ),
    );
    yield put(setBooksModalOpeningPending(true));
  } catch (err) {
    yield put(
      showNotificationErrorFromException(
        err,
        'Something happend trying to get updated playlist.',
      ),
    );
  }
}

function* updateChapterAudioSummaryWatcherSaga(action) {
  yield takeLatest(
    GET_UPDATE_CHAPTER_AUDIO_SUMMARY,
    updateChapterAudioSummaryWorkerSaga,
  );
}

function* exportQuestionsToCSVSagas() {
  yield takeLatest(EXPORT_QUESTIONS_TO_CSV_REQUEST, exportQuestionsToCSV);
}

function* exportQuestionsToCSV(action) {
  try {
    const { chapterId } = action.payload;

    yield put(
      Notifications.show(
        {
          title: 'Exporting',
          message: 'Generating questions CSV...',
          autoDismiss: 2,
        },
        'info',
      ),
    );

    const blob = yield call(api.exportQuestionsCSV, chapterId);

    // Trigger download
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `questions-chapter-${chapterId}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    yield put(
      Notifications.show(
        {
          title: 'Done!',
          message: 'Questions exported successfully!',
          autoDismiss: 3,
        },
        'success',
      ),
    );
  } catch (exception) {
    yield put(
      showNotificationErrorFromException(
        exception,
        'Could not export questions. Please try again later.',
      ),
    );
  }
}

function* exportChapterToCSVSagas() {
  yield takeLatest(EXPORT_CHAPTER_TO_CSV_REQUEST, exportChapterToCSV);
}

function* exportChapterToCSV(action) {
  try {
    const { chapterId } = action.payload;

    yield put(
      Notifications.show(
        {
          title: 'Exporting',
          message: 'Generating chapter CSV...',
          autoDismiss: 2,
        },
        'info',
      ),
    );

    const blob = yield call(api.exportChapterCSV, chapterId);

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `chapter-${chapterId}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    yield put(
      Notifications.show(
        {
          title: 'Done!',
          message: 'Chapter exported successfully!',
          autoDismiss: 3,
        },
        'success',
      ),
    );
  } catch (exception) {
    yield put(
      showNotificationErrorFromException(
        exception,
        'Could not export chapter. Please try again later.',
      ),
    );
  }
}

function* exportReferenceToCSVSagas() {
  yield takeLatest(EXPORT_REFERENCE_TO_CSV_REQUEST, exportReferenceToCSV);
}

function* exportReferenceToCSV(action) {
  try {
    const { referenceId } = action.payload;

    yield put(
      Notifications.show(
        {
          title: 'Exporting',
          message: 'Generating reference CSV...',
          autoDismiss: 2,
        },
        'info',
      ),
    );

    const blob = yield call(api.exportReferenceCSV, referenceId);

    // Trigger download
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `reference-${referenceId}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    yield put(
      Notifications.show(
        {
          title: 'Done!',
          message: 'Reference exported successfully!',
          autoDismiss: 3,
        },
        'success',
      ),
    );
  } catch (exception) {
    yield put(
      showNotificationErrorFromException(
        exception,
        'Could not export reference. Please try again later.',
      ),
    );
  }
}

function* importQuestionsFromCSVSagas() {
  yield takeLatest(IMPORT_QUESTIONS_FROM_CSV_REQUEST, importQuestionsFromCSV);
}

function* importQuestionsFromCSV(action) {
  try {
    const { chapterId, file, assignmentId } = action.payload;

    const result = yield call(
      api.importQuestionsFromCSV,
      chapterId,
      file,
      assignmentId,
    );

    yield put(closeModal());
    yield put(
      Notifications.show(
        {
          title: 'Success!',
          message: result.message,
          autoDismiss: 3,
        },
        'success',
      ),
    );

    yield put(
      loadQuestionsOfChapter(
        chapterId,
        1,
        50,
        assignmentId,
        false, // isInfiniteScroll
        null, // skip
        false, // isViewDeletedQuestions
        false, // loadAll
        null, // downloadCsv
        null, // deletedByAdmin
      ),
    );
  } catch (exception) {
    yield put(
      showNotificationErrorFromException(
        exception,
        'Could not import questions. Please try again later.',
      ),
    );
  }
}

function* importChapterFromCSVSagas() {
  yield takeLatest(IMPORT_CHAPTER_FROM_CSV_REQUEST, importChapterFromCSV);
}

function* importChapterFromCSV(action) {
  try {
    const { referenceId, file } = action.payload;
    yield call(api.importChapterFromCSV, referenceId, file);
    yield put(closeModal());
    yield put(
      Notifications.show(
        {
          title: 'Success!',
          message: `Importing chapter in background`,
          autoDismiss: 3,
        },
        'success',
      ),
    );
  } catch (exception) {
    yield put(
      showNotificationErrorFromException(
        exception,
        'Could not import chapter. Please try again later.',
      ),
    );
  }
}

function* importReferenceFromCSVSagas() {
  yield takeLatest(IMPORT_REFERENCE_FROM_CSV_REQUEST, importReferenceFromCSV);
}

function* importReferenceFromCSV(action) {
  try {
    const { file } = action.payload;

    yield call(api.importReferenceFromCSV, file);
    yield put(closeModal());
    yield put(
      Notifications.show(
        {
          title: 'Success!',
          message: `Importing reference in background`,
          autoDismiss: 3,
        },
        'success',
      ),
    );
  } catch (exception) {
    yield put(
      showNotificationErrorFromException(
        exception,
        'Could not import reference. Please try again later.',
      ),
    );
  }
}

/**
 * DEFAULT EXPORT
 */
export default function* sagas() {
  yield all([
    updateStudyProgramsSagas(),
    loadFiltersSagas(),
    createFilterSagas(),
    editFilterSagas(),
    setActiveFilterSagas(),
    deleteFilterSagas(),
    loadChapterQuestionsCSVSagas(),
    getLogsSagas(),
    getNamesToSecondBarSagas(),
    getChapterAudioPlaylistWatcherSagas(),
    updateChapterPlaylistWatcherSaga(),
    updateChapterAudioSummaryWatcherSaga(),
    exportQuestionsToCSVSagas(),
    exportChapterToCSVSagas(),
    exportReferenceToCSVSagas(),
    importQuestionsFromCSVSagas(),
    importChapterFromCSVSagas(),
    importReferenceFromCSVSagas(),
  ]);
}

function showNotificationErrorFromException(exception) {
  const message = getRequestErrorMessage(exception);

  return Notifications.show({ title: 'Ops!', message }, 'error');
}
